import axios from 'axios';
import { apiDomain } from '@/config';

import * as types from './types';

const actions = {
  resetState: ({ commit }) => {
    commit(types.RESET_STATE);
  },
  async fetchInitial({ commit, dispatch }) {
    commit(types.FETCH_INITIAL_PENDING);

    try {
      await Promise.all([
        dispatch('fetchProducts'),
        dispatch('fetchSubscriptions'),
        dispatch('fetchInvoices'),
      ]);

      commit(types.FETCH_INITIAL_FULFILLED);
    } catch (error) {
      commit(types.FETCH_INITIAL_ERROR);
    }
  },
  async fetchProducts({ commit }) {
    const response = await axios.get(`${apiDomain}subscriptions/products`);
    commit(types.FETCH_PRODUCTS_FULFILLED, response.data.data);
  },
  async fetchSubscriptions({ commit }) {
    const response = await axios.get(`${apiDomain}subscriptions`);
    commit(types.FETCH_SUBSCRIPTIONS_FULFILLED, response.data.data);
  },
  async fetchInvoices({ commit }) {
    const response = await axios.get(`${apiDomain}subscriptions/invoices`);
    commit(types.FETCH_INVOICES_FULFILLED, response.data.data);
  },
  async checkCouponCode({ commit }, form) {
    commit(types.CHECK_COUPON_CODE_PENDING);
    try {
      const url = `${apiDomain}subscriptions/coupons/check`;
      const response = await axios.post(url, form);
      commit(types.CHECK_COUPON_CODE_FULFILLED, response);
      return response;
    } catch (error) {
      commit(types.CHECK_COUPON_CODE_ERROR);
      return Promise.reject(error);
    }
  },
  async storeSubscriptionCoupon({ commit, dispatch }, form) {
    commit(types.STORE_COUPON_CODE_PENDING);
    try {
      const url = `${apiDomain}company/${form.company_id}/subscriptions/${form.subscription_id}/coupon`;
      const payload = {
        coupon_code: form.coupon_code,
      };
      const response = await axios.post(url, payload);
      dispatch('fetchInitial');
      dispatch('company/fetchCompanies', null, { root: true });
      commit(types.STORE_COUPON_CODE_FULFILLED, response);
      dispatch('messages/showMessage', { key: 'alert.storeSubscriptionCouponSuccessful', color: 'success' }, { root: true });
      return true;
    } catch (error) {
      commit(types.STORE_COUPON_CODE_ERROR);
      dispatch('messages/showMessage', { key: 'alert.storeSubscriptionCouponError', color: 'error' }, { root: true });
      return Promise.reject(error);
    }
  },
  async storeSubscription({ commit, dispatch }, form) {
    commit(types.STORE_SUBSCRIPTION_PENDING);
    try {
      const url = `${apiDomain}company/${form.company_id}/subscriptions`;
      const payload = {
        stripe_plan_id: form.stripe_id,
        token: form.credit_card_token,
        coupon_code: form.coupon_code,
      };
      const response = await axios.post(url, payload);
      dispatch('fetchInitial');
      dispatch('company/fetchCompanies', null, { root: true });
      commit(types.STORE_SUBSCRIPTION_FULFILLED, response);
      dispatch('messages/showMessage', { key: 'alert.storeSubscriptionSuccessful', color: 'success' }, { root: true });
      return true;
    } catch (error) {
      commit(types.STORE_SUBSCRIPTION_ERROR);
      dispatch('messages/showMessage', { key: 'alert.storeSubscriptionError', color: 'error' }, { root: true });
      return Promise.reject(error);
    }
  },
  async changeSubscription({ commit, dispatch }, form) {
    commit(types.CHANGE_SUBSCRIPTION_PENDING);
    try {
      const url = `${apiDomain}company/${form.company_id}/subscriptions/${form.subscription_id}/change`;
      const payload = {
        old_stripe_plan_id: form.old_stripe_plan_id,
        new_stripe_plan_id: form.new_stripe_plan_id,
        coupon_code: form.coupon_code,
      };
      const response = await axios.post(url, payload);
      await Promise.all([
        dispatch('fetchSubscriptions'),
        dispatch('fetchInvoices'),
        dispatch('company/fetchCompanies', null, { root: true }),
      ]);
      commit(types.CHANGE_SUBSCRIPTION_FULFILLED, response);
      // Message is handled in component
      return true;
    } catch (error) {
      commit(types.CHANGE_SUBSCRIPTION_ERROR);
      dispatch('messages/showMessage', { key: 'alert.updateSubscriptionError', color: 'error' }, { root: true });
      return Promise.reject(error);
    }
  },
  async storeAdditionalCompany({ commit, dispatch }, form) {
    commit(types.STORE_ADDITIONAL_COMPANY_PENDING);
    try {
      const url = `${apiDomain}company/${form.company_id}/subscriptions/${form.subscription_id}/additional`;
      const payload = {
        additional_company_id: form.additional_company_id,
      };
      const response = await axios.post(url, payload);
      dispatch('fetchInitial');
      dispatch('company/fetchCompanies', null, { root: true });
      commit(types.STORE_ADDITIONAL_COMPANY_FULFILLED, response);
      dispatch('messages/showMessage', { key: 'alert.storeAdditionalCompanySuccessful', color: 'success' }, { root: true });
      return true;
    } catch (error) {
      commit(types.STORE_ADDITIONAL_COMPANY_ERROR);
      dispatch('messages/showMessage', { key: 'alert.storeAdditionalCompanyError', color: 'error' }, { root: true });
      return Promise.reject(error);
    }
  },
  async cancelSubscription({ commit, dispatch }, form) {
    commit(types.CANCEL_SUBSCRIPTION_PENDING);
    try {
      let url = `${apiDomain}company/${form.company_id}/subscriptions/${form.subscription_id}`;
      let payload = {
        stripe_plan_id: form.stripe_plan_id,
      };
      if (form.type === 'additional') {
        url = `${url}/additional/reduce`;
        payload = {
          additional_company_id: form.additional_company_id,
        };
      }
      const response = await axios.post(url, payload);
      dispatch('fetchInitial');
      dispatch('company/fetchCompanies', null, { root: true });
      commit(types.CANCEL_SUBSCRIPTION_FULFILLED, response);
      dispatch('messages/showMessage', { key: 'alert.cancelSubscriptionSuccessful', color: 'success' }, { root: true });
      return true;
    } catch (error) {
      commit(types.CANCEL_SUBSCRIPTION_ERROR);
      dispatch('messages/showMessage', { key: 'alert.cancelSubscriptionError', color: 'error' }, { root: true });
      return Promise.reject(error);
    }
  },
  async changeCreditCard({ commit, dispatch }, form) {
    commit(types.CHANGE_CREDIT_CARD_PENDING);
    try {
      const url = `${apiDomain}company/${form.company_id}/subscriptions/creditcard`;
      const response = await axios.post(url, form);
      commit(types.CHANGE_CREDIT_CARD_FULFILLED, response);
      dispatch('messages/showMessage', { key: 'alert.changeCreditCardSuccessful', color: 'success' }, { root: true });
      return true;
    } catch (error) {
      commit(types.CHANGE_CREDIT_CARD_ERROR);
      dispatch('messages/showMessage', { key: 'alert.changeCreditCardError', color: 'error' }, { root: true });
      return Promise.reject(error);
    }
  },
  async fetchSubscriptionPDF({ commit }, form) {
    commit(types.FETCH_SUBSCRIPTION_PDF_PENDING);
    try {
      const url = `${apiDomain}company/${form.company_id}/subscriptions/invoices/${form.id}`;
      const response = await axios.get(url);
      commit(types.FETCH_SUBSCRIPTION_PDF_FULFILLED);
      return response;
    } catch (error) {
      commit(types.FETCH_SUBSCRIPTION_PDF_ERROR);
      return Promise.reject(error);
    }
  },
};

export default actions;
